import {
  Zap, Search, Eye, Rocket, Bug, Star, Globe, FolderOpen,
} from 'lucide-react';
import type { Preset } from '../types/preset';
import { PresetScope } from '../types/preset';

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Zap, Search, Eye, Rocket, Bug, Star, Globe, FolderOpen,
};

interface PresetPickerProps {
  presets: Preset[];
  selectedId: string | null;
  onSelect: (preset: Preset | null) => void;
}

function getScopeLabel(scope: PresetScope): string {
  switch (scope) {
    case PresetScope.BUILTIN: return 'Built-in';
    case PresetScope.GLOBAL: return 'My Presets';
    case PresetScope.PROJECT: return 'Project';
    default: return scope;
  }
}

export default function PresetPicker({ presets, selectedId, onSelect }: PresetPickerProps) {
  // Group by scope
  const grouped = presets.reduce<Record<string, Preset[]>>((acc, preset) => {
    const key = preset.scope;
    if (!acc[key]) acc[key] = [];
    acc[key].push(preset);
    return acc;
  }, {});

  const scopeOrder: PresetScope[] = [PresetScope.BUILTIN, PresetScope.GLOBAL, PresetScope.PROJECT];

  return (
    <div className="space-y-3">
      {scopeOrder.map((scope) => {
        const items = grouped[scope];
        if (!items?.length) return null;

        return (
          <div key={scope}>
            {scope !== PresetScope.BUILTIN && (
              <div className="mb-1.5 text-xs font-medium text-slate-400 uppercase tracking-wide">
                {getScopeLabel(scope)}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              {items.map((preset) => {
                const Icon = ICON_MAP[preset.icon] || Zap;
                const isSelected = selectedId === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => onSelect(isSelected ? null : preset)}
                    className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-center transition-all ${
                      isSelected
                        ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <span style={{ color: preset.color || undefined }}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <div className="text-xs font-medium text-slate-900">
                      {preset.name}
                    </div>
                    <div className="text-[10px] text-slate-400">
                      ${preset.maxBudgetUsd ?? '?'}/{preset.maxTurns ?? '?'}t
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {presets.length === 0 && (
        <div className="py-4 text-center text-sm text-slate-400">
          No presets available
        </div>
      )}
    </div>
  );
}
